import { BCMSCurrency, Branding, BrandingStyle, DisabledPrompt, Link, useModel, useRoute } from "@bcm-dev/bcms-vite";
import Util from "classes/Util";
import { WidgetContainer, WidgetView } from 'components/shared';
import { Account, CheckingAccount, CODAccount, LoanAccount, LOCAccount, SavingsAccount } from "model";
import React, { useEffect, useState } from "react";
import AccountSummaryBrandableLinksWidget from "./components/AccountSummaryBrandableLinksWidget";
import "./styles.css";
import "./mobile.css";
import AccountSummaryTotalRow from "./components/AccountSummaryTotalRow";

/**
 * @function AccountSummaryWidget
 * @return {React.Component}
 * @exported true
 * @description This is the quick transfer widget
 */
export default function AccountSummaryWidget(props) {
  const { branding } = useModel();
  const [ isDesktop, setIsDesktop ] = useState(window.innerWidth >= 751);
  const isMobile = !isDesktop; 
  const { options } = branding;
  const hasAccountOwnerNameOn = options.acctownername_abi22 === "true";
  const hasDesktopAvailableBalanceOn = options.availbal_abi22 === "true";
  const hasDesktopCurrentBalanceOn = options.currentbal_abi22 === "true";
  const hasDesktopTotalAccountBalancesOn = options.accttotals_acctsum_abi22 === "true";
  const hasSectionHeaders = options.section_headers_abi22 === "true";
  let hasTotalAccountBalancesOn = hasDesktopTotalAccountBalancesOn;
  let hasAvailableBalanceOn = false;
  let hasCurrentBalanceOn = false;
  let totalAccountBalancesPosition = options.account_totals_above_or_below_abi22 === "undefined" ? "Above" : options.account_totals_above_or_below_abi22;
  let showHeaderTotalAccountBalances = totalAccountBalancesPosition === "Above";

  // START: Get balance branding from mabi23 if set.
  const mabi23Branding = Util.getMABI23Branding();
  let mabi23Options = {};

  if(mabi23Branding) {
    mabi23Options = mabi23Branding.options;
  }
  // END: Get balance branding from mabi23 if set.

  // "isDesktop" is dynamically updated in the "useEffect" hook below.
  if(isDesktop) {
    hasTotalAccountBalancesOn = hasDesktopTotalAccountBalancesOn;
    hasAvailableBalanceOn = hasDesktopAvailableBalanceOn;
    hasCurrentBalanceOn = hasDesktopCurrentBalanceOn;
  } else if(isMobile) {
    // Get balance branding from mabi23 if set.
    if(mabi23Branding) {
      hasTotalAccountBalancesOn = mabi23Options.accttotals_acctsum_mabi23 === "true";
      totalAccountBalancesPosition = mabi23Options.account_totals_above_or_below_mabi23 === "undefined" ? "Above" : mabi23Options.account_totals_above_or_below_mabi23;
    }

    showHeaderTotalAccountBalances = totalAccountBalancesPosition === "Above";
    hasAvailableBalanceOn = true;
    hasCurrentBalanceOn = false;
  }

  // Create the Summary items for each type of account
  const hasLOCAccounts = options.loc_primary_abi22 === "true";
  const firstAccounts = options.loc_primary_abi22 === "true" ? LOCAccount.initAll() : CheckingAccount.initAll();
  // to do: make a shared Account method to return either all asset accounts or all loc accounts.
  const firstAccts = firstAccounts.map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } isDesktop={ isDesktop } amount={ account.availableBalance } label={ account.balanceLabel } 
  hasAccountOwnerNameOn={ hasAccountOwnerNameOn } hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />, []);

  let savingsAccts = [];

  if(!hasLOCAccounts) {
    savingsAccts = SavingsAccount.initAll().map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } isDesktop={ isDesktop } amount={ account.availableBalance } label={ account.balanceLabel }
      hasAccountOwnerNameOn={ hasAccountOwnerNameOn } hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />, []);    
  }
  
  const codAccounts = CODAccount.initAll().map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } isDesktop={ isDesktop } amount={ account.availableBalance } label={ account.balanceLabel }
  hasAccountOwnerNameOn={ hasAccountOwnerNameOn } hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />, []);
  
  const loanAccts = LoanAccount.initAll().map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } isDesktop={ isDesktop } amount={ account.currentBalance } label={ account.balanceLabel }
  hasAccountOwnerNameOn={ hasAccountOwnerNameOn } hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />, []);

  const totalAccountBalances = (
  accountType, accounts, showAvailableBalance, showCurrentBalance, showAvailableCredit,
  availableBalanceDataPoint, availableCreditDataPoint, currentBalanceDataPoint
  ) => {
    const accumulator = { 
        "accountType": accountType,
        "availableBalance": (showAvailableBalance === false || showAvailableBalance === undefined) ? undefined : 0, 
        "availableCredit": (showAvailableCredit === false || showAvailableCredit === undefined) ? undefined : 0, 
        "currentBalance": (showCurrentBalance === false || showCurrentBalance === undefined) ? undefined : 0,
        "availableBalanceDataPoint": availableBalanceDataPoint,
        "availableCreditDataPoint": availableCreditDataPoint,
        "currentBalanceDataPoint": currentBalanceDataPoint
    };

    return accounts.reduce((acc, item) => {
        if(acc.availableBalance !== undefined) {
          if(Account.showBalanceTypeDependingOnViewType(item, "available", isDesktop ? "desktop" : "mobile")) {
            acc.availableBalance += parseFloat(item.availableBalance ? item.availableBalance.amount : 0);
          }
        }
        if(acc.currentBalance !== undefined) {
          if(Account.showBalanceTypeDependingOnViewType(item, "current", isDesktop ? "desktop" : "mobile")) {
            acc.currentBalance += parseFloat(item.currentBalance ? item.currentBalance.amount : 0);
          }
        }
        if(acc.availableCredit !== undefined) {
            acc.availableCredit += parseFloat(item.availableCredit ? item.availableCredit.amount : 0);
        }
        return acc;
    }, accumulator);
  };

  // If any account has a balance by "type" (availble/current, ect.) 
  // branded to be on, the total balance of that type is shown.
  const showTotalBalanceByType = (type, accounts) => {
      return accounts.filter(account => {
          return Account.showBalanceTypeDependingOnViewType(account, type, isDesktop ? "desktop" : "mobile");
      }).length > 0;
  };

  const totalCheckingAccountBalancesObj = totalAccountBalances(
    hasLOCAccounts ? "checking" : "loc", 
    firstAccounts, 
    showTotalBalanceByType("available", firstAccounts), 
    showTotalBalanceByType("current", firstAccounts),
    undefined,
    "Checking_Accounts"
  );
  
  let totalSavingsAccountBalancesObj = {};

  if(!hasLOCAccounts) {
    totalSavingsAccountBalancesObj = totalAccountBalances(
      "savings", SavingsAccount.initAll(), 
      showTotalBalanceByType("available", SavingsAccount.initAll()), 
      showTotalBalanceByType("current", SavingsAccount.initAll())
    );
  }

  const totalCODAccountBalancesObj = totalAccountBalances(
    "cod", CODAccount.initAll(), 
    showTotalBalanceByType("available", CODAccount.initAll()), 
    showTotalBalanceByType("current", CODAccount.initAll())
  );
  //const totalLoanAccountBalancesObj = totalAccountBalances("loan", LoanAccount.initAll(), showTotalBalanceByType("available", LoanAccount.initAll()), showTotalBalanceByType("current", LoanAccount.initAll()), false);

    // START: Assets/Loans sections.
    const showAssetsAndLoansUI = String(props.showAssetsAndLoansUI ?? "false") === "true";
    let allAssetAccounts = [];
    let allLoanAccounts = [];

    if(hasLOCAccounts) {
        allLoanAccounts = [ ...LOCAccount.initAll(), ...LoanAccount.initAll() ];
    } else {
        allAssetAccounts = [ ...CheckingAccount.initAll(), ...SavingsAccount.initAll() ];
        allLoanAccounts = LoanAccount.initAll();
    }

    allAssetAccounts = allAssetAccounts.concat(CODAccount.initAll());

    const totalAssetAvailableBalancesObj = totalAccountBalances(
      "all", 
      allAssetAccounts, 
      showTotalBalanceByType("available", allAssetAccounts), 
      showTotalBalanceByType("current", allAssetAccounts),
      undefined,
      "Assets_Label"
    );

    const totalLoanAccountBalancesObj = totalAccountBalances(
      "loan", 
      allLoanAccounts, 
      showTotalBalanceByType("available", LoanAccount.initAll()), 
      showTotalBalanceByType("current", LoanAccount.initAll()), 
      undefined,
      undefined,
      undefined,
      "Loans"
    );

    const allAssetAccountViews = allAssetAccounts.map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } isDesktop={ isDesktop } amount={ account.availableBalance } label={ account.balanceLabel } 
    hasAccountOwnerNameOn={ hasAccountOwnerNameOn } hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />, []);

    const allLoanAccountViews = allLoanAccounts.map(account => <AccountSummaryView key={ "account_" + account.id } account={ account } isDesktop={ isDesktop } amount={ account.currentBalance } label={ account.balanceLabel }
    hasAccountOwnerNameOn={ hasAccountOwnerNameOn } hasAvailableBalanceOn={ hasAvailableBalanceOn } hasCurrentBalanceOn={ hasCurrentBalanceOn } />, []);
    // END: Assets/Loans sections.

  useEffect(() => {
    // Detect window resize to apply desktop only options and mobile only options.
    const onWindowResize = () => setIsDesktop(window.innerWidth >= 751);
    window.addEventListener("resize", onWindowResize);
    onWindowResize();
    return () => window.removeEventListener("resize", onWindowResize);
  });

  return(
    <WidgetContainer className={"account-summary-widget "+props.className}>
      <WidgetView text="Account Summary" dataPoint="Account_Summary_Header">
        <AccountSummaryBrandableLinksWidget/>
        <AccountSummaryTable>
          
        {/* START: Assets/Loans sections. */}
        { showAssetsAndLoansUI && 
        <>
          { hasSectionHeaders &&
          <>
              <AccountSummaryHeader dataPoint="Assets_Label" text={ `Assets` }
              hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
              showTotalAccountBalances={ showHeaderTotalAccountBalances }
              totalAccountBalancesObj={ totalAssetAvailableBalancesObj } />
              { allAssetAccountViews }
              <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
              totalAccountBalancesObj={ totalAssetAvailableBalancesObj }
              totalColumns={ 2 }
              showTotalAccountBalances={ !showHeaderTotalAccountBalances } />
              { !Util.inMabi && 
                  <Branding id="acctsumwidget_order_checks_abi22" value="true" default="false">
                  <>
                      <DisabledPrompt>
                      <Link>
                          <span data-point="Order_Checks" className="order-checks-link">Order Checks</span>
                      </Link>
                      </DisabledPrompt>
                  </>
                  </Branding>
              }
              <AccountSummaryHeader dataPoint="Loans" text="Loans" 
              hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
              showTotalAccountBalances={ showHeaderTotalAccountBalances }
              totalAccountBalancesObj={ totalLoanAccountBalancesObj } />
              { allLoanAccountViews }
              <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
              showTotalAccountBalances={ !showHeaderTotalAccountBalances }
              totalColumns={ 2 }
              totalAccountBalancesObj={ totalLoanAccountBalancesObj } />
          </>
          }
          { !hasSectionHeaders &&
           <>
           { firstAccts }
           <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
            totalAccountBalancesObj={ totalCheckingAccountBalancesObj } 
            totalColumns={ 2 }
            showTotalAccountBalances={ !showHeaderTotalAccountBalances } />
           { !Util.inMabi && 
              <Branding id="acctsumwidget_order_checks_abi22" value="true" default="false">
                <>
                  <DisabledPrompt>
                    <Link>
                      <span data-point="Order_Checks" className="order-checks-link">Order Checks</span>
                    </Link>
                  </DisabledPrompt>
                </>
              </Branding>
            }
           { !hasLOCAccounts && savingsAccts }
           <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
            totalAccountBalancesObj={ totalSavingsAccountBalancesObj } 
            totalColumns={ 2 }
            showTotalAccountBalances={ !showHeaderTotalAccountBalances } />
           { codAccounts }
           <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
            totalAccountBalancesObj={ totalCODAccountBalancesObj } 
            totalColumns={ 2 }
            showTotalAccountBalances={ !showHeaderTotalAccountBalances } />
           { loanAccts }
           <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
            totalAccountBalancesObj={ totalLoanAccountBalancesObj } 
            totalColumns={ 2 }
            showTotalAccountBalances={ !showHeaderTotalAccountBalances } />
           </> 
           }
        </>
        }
        {/* END: Assets/Loans sections. */}

        {/* START: Default UI. Branded assets/Loans sections are not shown. */}
        { !showAssetsAndLoansUI &&
         <>
          { hasSectionHeaders && 
          <AccountSummaryHeader dataPoint="Checking_Accounts" text={ hasLOCAccounts ? `Line of Credit` : `Checking Accounts` }
          hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
          showTotalAccountBalances={ showHeaderTotalAccountBalances }
          totalAccountBalancesObj={ totalCheckingAccountBalancesObj } />
          }
            { firstAccts }
            <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
            totalAccountBalancesObj={ totalCheckingAccountBalancesObj } 
            totalColumns={ 2 }
            showTotalAccountBalances={ !showHeaderTotalAccountBalances } />
            { !Util.inMabi && 
              <Branding id="acctsumwidget_order_checks_abi22" value="true" default="false">
                <>
                  <DisabledPrompt>
                    <Link>
                      <span data-point="Order_Checks" className="order-checks-link">Order Checks</span>
                    </Link>
                  </DisabledPrompt>
                </>
              </Branding>
            }
          { hasSectionHeaders && !hasLOCAccounts &&
          <AccountSummaryHeader text="Saving Accounts" 
          hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
          showTotalAccountBalances={ showHeaderTotalAccountBalances }
          totalAccountBalancesObj={ totalSavingsAccountBalancesObj } />
          }
            { !hasLOCAccounts && savingsAccts }
            <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
            totalAccountBalancesObj={ totalSavingsAccountBalancesObj } 
            totalColumns={ 2 }
            showTotalAccountBalances={ !showHeaderTotalAccountBalances } />

          { hasSectionHeaders && 
          <AccountSummaryHeader text="Certificate of Deposit" 
          hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
          showTotalAccountBalances={ showHeaderTotalAccountBalances }
          totalAccountBalancesObj={ totalCODAccountBalancesObj } />
          }
            { codAccounts }
            <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
            totalAccountBalancesObj={ totalCODAccountBalancesObj } 
            totalColumns={ 2 }
            showTotalAccountBalances={ !showHeaderTotalAccountBalances } />

          { hasSectionHeaders && 
          <AccountSummaryHeader dataPoint="Loans" text="Loans" 
          hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
          showTotalAccountBalances={ showHeaderTotalAccountBalances }
          totalAccountBalancesObj={ totalLoanAccountBalancesObj } />
          }
            { loanAccts }
            <AccountSummaryTotalRow hasTotalAccountBalancesOn={ hasTotalAccountBalancesOn } 
            totalAccountBalancesObj={ totalLoanAccountBalancesObj } 
            totalColumns={ 2 }
            showTotalAccountBalances={ !showHeaderTotalAccountBalances } />
         </> 
         }
         {/* END: Default UI. Branded assets/Loans sections are not shown. */}

        </AccountSummaryTable>
      </WidgetView>
    </WidgetContainer>
  );
}

/**
 * @function AccountSummaryView
 * @param {Account} account - Instance of Account
 * @param {bcmsCurrency} amount - Amount value
 * @param {STRING} label - The label for the summary item
 * @exported true
 * @description How an account is presented in account summary
 * @note If amount is not an instance of BCMSCurrency an amount of "$0.00" is shown
 */
export function AccountSummaryView({ account, isDesktop, amount, label, hasAccountOwnerNameOn, hasAvailableBalanceOn, hasCurrentBalanceOn }) {
  const balance = amount instanceof BCMSCurrency ? amount.format() : BCMSCurrency.toCurrency(0);

  return (
    <SummaryItem id={ account.id } account={ account } isDesktop={ isDesktop } accountName={ account.nickname } availableBalance={ balance }
     accountNumber={ account.number } balanceLabel={ label } profile={ account.profile } 
     hasAccountOwnerNameOn={ hasAccountOwnerNameOn } hasAvailableBalanceOn={ hasAvailableBalanceOn }
    hasCurrentBalanceOn={ hasCurrentBalanceOn } />
  );
}

/**
 * @function AccountSummaryTable
 * @param {OBJECT} props 
 * @return {React.Component}
 * @exported true
 * @description This is the table for all of the table components
 * @example
 * <AccountSummaryTable>
 *  <AccountSummaryHeader text="This is a header" />
 * </AccountSummaryTable>
 */
export function AccountSummaryTable(props) {
  return(
    <table className="account-summary-table">
      {props.children}
    </table>
  );
}

/**
 * @function AccountSummaryHeader
 * @param {OBJECT} props
 * @param {STRING} text - This is the white text that is shown in the blue header.
 * @return {React.Component}
 * @exported true
 * @description The colored header row that shows the account types
 * @example
 * <AccountSummaryHeader text="This is a sample header">
 */
export function AccountSummaryHeader(props) {
  const text = props.text ?? "";
  const dataPoint = props.dataPoint ?? "";
  const hasTotalAccountBalancesOn = props.hasTotalAccountBalancesOn ?? false;
  const totalAccountBalancesObj = props.totalAccountBalancesObj ?? {};
  const showTotalAccountBalances = props.showTotalAccountBalances ?? true;

  // Returns a total balances string like "$26,125.00 | $26,125.00"
  // for the total available balance (left) and the total current balance (right).
  // Only loans have "availableCredit" but that balance not being used at the moment.
  const totalBalanceMessage = obj => {
    let message = "";

    if(obj.availableBalance !== undefined) {
      message += `${ BCMSCurrency.init(obj.availableBalance).format() } | `;
    }
    if(obj.availableCredit !== undefined) {
      message += `${ BCMSCurrency.init(obj.availableCredit).format() } | `;
    }
    if(obj.currentBalance !== undefined) {
      message += `${ BCMSCurrency.init(obj.currentBalance).format() } | `;
    }
    message = message.slice(0, -2);
    return message;
  }
  
  const totalAccountBalanceText = totalBalanceMessage(totalAccountBalancesObj);


  return (
    <thead>
      <tr>
        { (hasTotalAccountBalancesOn && showTotalAccountBalances) && 
          <>
            <th className="text-left" colSpan="1">
                <span data-point={dataPoint}>
                { text }
              </span>
            </th>
            <th className="text-right" colSpan="1">
                <span data-point={dataPoint}>
                { totalAccountBalanceText }
              </span>
            </th>
          </>
        }
        { !(hasTotalAccountBalancesOn && showTotalAccountBalances) && 
          <th className="text-left" colSpan="2">
              <span data-point={dataPoint}>
              { text }
            </span>
          </th>
        }
      </tr>
    </thead>
  );
}

/**
 * @function SummaryItem
 * @param {OBJECT} props 
 * @param {STRING} accountName - The account name
 * @param {STRING} availableBalance - Available balance for the account.
 * @param {STRING} accountNumber - Short four digit account number.
 * @return {React.Component}
 * @exported true
 * @description This is the component that shows each account and the account details.
 * @example
 * <SummaryItem accountName="Example Account" availableBalance="$7,498.24" accountNumber="*6238">
 */
export function SummaryItem(props) {
  const account = props.account ?? {};
  const isDesktop = props.isDesktop ?? true;
  const isLoanOrLOCAccount = Account.isLoanOrLOC(account);
  const accountName = props.accountName ?? "";
  const profileName = props.profile.name;
  const accountNumber = props.accountNumber ?? "";
  const balanceLabel = props.balanceLabel ?? "Available Balance";
  const secondaryBalanceLabel = account.secondaryBalanceLabel ?? "";
  const accountID = props.id ?? "";
  const hasAccountOwnerNameOptionOn = props.hasAccountOwnerNameOn ?? false;
  const currAccountHasAvailableBalanceOn = Account.showBalanceTypeDependingOnViewType(account, "available", isDesktop ? "desktop" : "mobile");
  const currAccountHasCurrentBalanceOn = Account.showBalanceTypeDependingOnViewType(account, "current", isDesktop ? "desktop" : "mobile");
  let defaultView =  window.innerWidth <= 750? "mobile_":"Desktop_";

  const accountDescription = hasAccountOwnerNameOptionOn ? `${ accountNumber } | ${ profileName }` : accountNumber;
  const navigate = useRoute();  
  let onClick = () => {        
          navigate(`/accountdetails/${accountID}`);        
  }

  let accountNameDataPoint = `Account_Name_${(props.view?props.view+"_":defaultView)+ accountID }`;
  let accountAvailableBalanceDataPoint = `Account_Balance_${(props.view?props.view+"_":defaultView)+ accountID }`;
  let accountCurrentBalanceDataPoint = `Account_Current_${(props.view?props.view+"_":defaultView)+ accountID }`;

  // If "Available Balance" is off, move the data point on that amount to the "Current Balance" amount.
  // This is because this particular data point is used in the following captures:
  // https://onlinebanktours.com/bcms/edit/pages/captures/edit.php?id=14801
  // https://onlinebanktours.com/bcms/edit/pages/captures/edit.php?id=20268
  if(!currAccountHasAvailableBalanceOn) {
    accountCurrentBalanceDataPoint = accountAvailableBalanceDataPoint;
  }

  return(
    <tbody className="clickable" onClick={onClick}>
      <tr className="summary-item " >
        <td className="account-name">
          <BrandingStyle style={{"color": "acct_name_abi22"}} element={
            <span data-point={ accountNameDataPoint }>
             { accountName }
            </span>
          }/>
        </td>
        { currAccountHasAvailableBalanceOn &&
          <td className="text-right available-balance">
            <span data-point={ accountAvailableBalanceDataPoint }>
              { account.availableBalance.format() }
            </span>
          </td>
        }
        { !currAccountHasAvailableBalanceOn && currAccountHasCurrentBalanceOn &&
          <td className="text-right available-balance">
          <span data-point={ accountCurrentBalanceDataPoint }>
              { account.currentBalance.format() }
            </span>
          </td>
        }
      </tr>
      <tr className="summary-item">
        <td className="sub-text">
          <span data-point={ `Account_Number_${ (props.view?props.view+"_":defaultView)+accountID }` }>
            { accountDescription }
          </span>
        </td>
        { currAccountHasAvailableBalanceOn &&
          <td className="text-right">
            <span>
              { balanceLabel }
            </span>
          </td>
        }
        { !currAccountHasAvailableBalanceOn && currAccountHasCurrentBalanceOn &&
          <td className="text-right">
            <span>
              { isLoanOrLOCAccount ? "Current Balance" : secondaryBalanceLabel }
            </span>
          </td>
        }
      </tr>
      {/* START: Row to show the current balance beneath the available balance. Only appears in asset accounts. */}
      { currAccountHasAvailableBalanceOn && currAccountHasCurrentBalanceOn &&
      <>
        <tr className="summary-item">
          <td className="account-name"></td>
          <td className="text-right available-balance">
            <span data-point={ `Account_Current_${(props.view?props.view+"_":defaultView)+ accountID }` }>
              { account.currentBalance.format() }
            </span>
          </td>
        </tr>
        <tr className="summary-item">
          <td className="sub-text"></td>
          <td className="text-right">
            <span>
              { secondaryBalanceLabel }
            </span>
          </td>
        </tr>
      </>
      }
      {/* END: Row to show the current balance beneath the available balance. Only appears in asset accounts. */}
    </tbody>
  );
}
